import { CommonModule } from '@angular/common';
import { Component, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormlyModule } from '@ngx-formly/core';
import { getTimezonesWithOffsets, momentTimezone } from '@vectronic/util';
import { map, Observable, startWith, tap } from 'rxjs';
import { CustomErrorStateMatcher } from '../../utils/custom-error-state-matcher';

type Timezone = {
  name: string;
  offset: number;
};

@Component({
  selector: 'vectronic-formly-timezone-select',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    FormlyModule,
  ],
  providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: TimezoneSelectComponent }],
  templateUrl: './timezone-select.component.html',
  styleUrls: ['./timezone-select.component.scss'],
})
export class TimezoneSelectComponent implements ControlValueAccessor, OnInit {
  searchControl = new FormControl<string>('');
  timezones: Timezone[];
  filteredTimezones$: Observable<Timezone[]>;

  // `invalid` lets a parent (e.g. a formly wrapper) signal that this control should be
  // shown as errored due to state - such as `required` - that `searchControl` itself never carries.
  matcher: ErrorStateMatcher = {
    isErrorState: (control: FormControl | null): boolean => {
      const ownErrorState = new CustomErrorStateMatcher().isErrorState(control);
      return ownErrorState || this.invalid();
    },
  };

  private timezoneRegex = /[+-]\d{2}:\d{2}/;
  name = input<string>();
  formlyAttributes = input();
  label = input<string>();
  id = input<string>();
  readonly = input<boolean>();
  tabIndex = input<number>();
  placeholder = input<string>();
  required = input<boolean>(false);
  invalid = input<boolean>(false);

  onChange: (value: number) => void;
  onTouched: () => void;

  writeValue(value: number): void {
    this.searchControl.setValue(this.getNameByOffset(value));
  }

  registerOnChange(fn: (value?: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  constructor() {
    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        tap((timezoneName) => {
          if (!this.searchControl.errors) {
            this.onChange?.(this.getOffsetByName(timezoneName));
            this.onTouched?.();
          }
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    this.timezones = getTimezonesWithOffsets()
      .filter((zone) => !(zone.name.includes('Etc/') || zone.name.includes('GMT')))
      .map((zone) => this.formatZone(zone.name));
    this.filteredTimezones$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value.toString() ?? '', this.timezones)),
    );
    this.searchControl.addValidators(this.timezoneValidator);
  }

  formatZone(zone: string) {
    const foundZone = momentTimezone(undefined, zone);
    const utc: string = foundZone.format('Z');
    return {
      name: `${zone} (${utc})`,
      //timezone based on minutes / 15
      offset: foundZone.utcOffset() / 15,
    };
  }

  private _filter(value: string, timezones: Timezone[]) {
    value = value.toLowerCase();
    return timezones.filter((timezone) => timezone.name?.toLowerCase().includes(value));
  }

  timezoneValidator = (control: AbstractControl): ValidationErrors | null => {
    const validOptions = this.timezones.map((timezone) => timezone.name);
    const selectedOptionIsValid =
      !control.getRawValue() || validOptions.includes(control.getRawValue());
    return selectedOptionIsValid ? null : { invalidOption: { message: 'Invalid option' } };
  };

  private getOffsetByName(timezoneName: string): number {
    return this.timezones.find((tz) => tz.name === timezoneName)?.offset;
  }

  private getNameByOffset(offset: number): string {
    return this.timezones.find((tz) => tz.offset === offset)?.name || '';
  }

  protected displayFn(): (name: string) => string {
    return (name: string) => {
      const correspondingTZ = this.timezones.find((tz) => tz.name === name);
      const matchedResult = correspondingTZ?.name.match(this.timezoneRegex);
      return matchedResult ? matchedResult[0] : '';
    };
  }
}
